import React, {useContext, useState} from "react";
import "./MemberList.css";
import {Link} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {
  GroupDetailResponse,
  GroupMemberResponse,
} from "../../../logic/APITypes";
import API from "../../../logic/API";
import {KeycloakContext} from "../../contextProviders/KeycloakProvider";
import {ModalContext} from "../../contextProviders/ModalProvider";
import {SnackbarContext} from "../../../utility/SnackbarProvider";

type GroupMemberListProps = {
  group: {
    get: GroupDetailResponse & {groupId: string};
    set: (
      updatePayload: (group: GroupDetailResponse) => GroupDetailResponse,
    ) => void;
  };
  members: {
    get: GroupMemberResponse[];
    set: (
      updatePayload: (group: GroupMemberResponse[]) => GroupMemberResponse[],
    ) => void;
  };
};

/** A tile element of the apps list containing overview information of installed applications */
function GroupMemberList({group, members}: GroupMemberListProps) {
  const {formatMessage} = useIntl();
  const {keycloak, isSchAdmin} = useContext(KeycloakContext);
  const modalContext = useContext(ModalContext);
  const {addSnackbar} = useContext(SnackbarContext);

  const [blockMemberActions, setBlockMemberActions] = useState<
    Record<string, boolean>
  >({all: false});

  const changeRights = (member: GroupMemberResponse) => {
    API.updateGroupMember(group.get.groupId, member.id, !member.is_admin).then(
      (response) => {
        if (response.success) {
          if (member.is_admin) {
            group.set((old) => ({
              ...old,
              admins: old.admins.filter((a) => a.email != member.email),
            }));
          } else
            group.set((old) => ({
              ...old,
              admins: [...old.admins, member],
            }));
          members.set((old) =>
            old.map((m) =>
              m.id !== member.id ? m : {...m, is_admin: !m.is_admin},
            ),
          );
        } else
          addSnackbar({
            variant: "close",
            text: formatMessage({
              id: "groupMemberList.snackbar.update.error",
            }),
            hasCloseButton: true,
          });
        setBlockMemberActions((old) => ({
          ...old,
          [member.id]: false,
          all: false,
        }));
      },
    );
  };

  return (
    <div className="groupMemberList">
      <span className="groupMemberList__header">
        <FormattedMessage id={"groupMemberList.header.name"} />
      </span>
      <span className="groupMemberList__header">
        <FormattedMessage id={"groupMemberList.header.mail"} />
      </span>
      <span className="groupMemberList__header groupMemberList__centered">
        <FormattedMessage id={"groupMemberList.header.promote"} />
      </span>
      <span className="groupMemberList__header groupMemberList__centered">
        <FormattedMessage id={"groupMemberList.header.remove"} />
      </span>
      {members.get.map((user) => (
        <React.Fragment key={user.id}>
          <span
            className={
              "groupMemberList__name" +
              (user.id === keycloak.subject ? " groupMemberList__subject" : "")
            }
          >
            {user.first_name + " " + user.last_name}
          </span>
          <Link
            className={
              "groupMemberList__mail" +
              (user.id === keycloak.subject ? " groupMemberList__subject" : "")
            }
            to={"mailto:" + user.email}
          >
            {user.email}
          </Link>
          <button
            className="linkButton groupMemberList__centered"
            disabled={blockMemberActions["all"] || blockMemberActions[user.id]}
            onClick={() => {
              if (user.id !== keycloak.subject || isSchAdmin) {
                setBlockMemberActions((old) => ({...old, [user.id]: true}));
                changeRights(user);
              } else {
                setBlockMemberActions((old) => ({...old, all: true}));
                modalContext.showModal({
                  headline: formatMessage({
                    id: "groupMemberList.modal.selfDemote.headline",
                  }),
                  body: formatMessage({
                    id: "groupMemberList.modal.selfDemote.body",
                  }),
                  primaryButton: {
                    name: formatMessage({
                      id: "groupMemberList.modal.selfDemote.confirm",
                    }),
                    onClick: () => {
                      changeRights(user);
                      location.reload();
                    },
                  },
                  secondaryButton: {
                    name: formatMessage({
                      id: "groupMemberList.modal.selfDemote.cancel",
                    }),
                    onClick: () => {
                      modalContext.hideModal();
                      setBlockMemberActions((old) => ({
                        ...old,
                        all: false,
                        [user.id]: false,
                      }));
                    },
                  },
                });
              }
            }}
          >
            <FormattedMessage
              id={"groupMemberList." + (user.is_admin ? "demote" : "promote")}
            />
          </button>
          <button
            className="linkButton groupMemberList__centered"
            disabled={
              blockMemberActions["all"] ||
              blockMemberActions[user.id] ||
              user.id === keycloak.subject
            }
            onClick={() => {
              setBlockMemberActions((old) => ({...old, [user.id]: true}));
              modalContext.showModal({
                headline: formatMessage({
                  id: "groupMemberList.modal.remove.headline",
                }),
                body: formatMessage(
                  {
                    id: "groupMemberList.modal.remove.body",
                  },
                  {
                    memberName: user.first_name + " " + user.last_name,
                    memberMail: user.email,
                  },
                ),
                primaryButton: {
                  name: formatMessage({
                    id: "groupMemberList.modal.remove.confirm",
                  }),
                  onClick: () => {
                    API.removeGroupMember(group.get.groupId, user.id).then(
                      (response) => {
                        if (response.success) {
                          group.set((old) => ({
                            ...old,
                            member_count: old.member_count - 1,
                            admins: old.admins.filter(
                              (a) => a.email !== user.email,
                            ),
                          }));
                          members.set((old) =>
                            old.filter((m) => m.id !== user.id),
                          );
                        } else
                          addSnackbar({
                            variant: "close",
                            text: formatMessage({
                              id: "groupMemberList.snackbar.remove.error",
                            }),
                            hasCloseButton: true,
                          });
                        setBlockMemberActions((old) => ({
                          ...old,
                          [user.id]: false,
                        }));
                      },
                    );
                  },
                },
                secondaryButton: {
                  name: formatMessage({
                    id: "groupMemberList.modal.remove.cancel",
                  }),
                  onClick: () => {
                    modalContext.hideModal();
                    setBlockMemberActions((old) => ({
                      ...old,
                      [user.id]: false,
                    }));
                  },
                },
              });
            }}
          >
            <FormattedMessage id={"groupMemberList.remove"} />
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

export default GroupMemberList;
